// App-wide color theme. Each theme is a token block in global.css keyed on a
// `data-theme` attribute (set on <html>); this module just tracks the current id
// and persists it. Persisted to localStorage so it applies SYNCHRONOUSLY at boot
// (no flash) and stays per-device.
//
// A theme's `base` ('dark' | 'light') tells the rest of the app which way it
// leans — notably the CodeMirror editor, which picks a dark or light editor
// theme to match (see CodeMirrorSQL.jsx).
import { createSignal } from 'solid-js';
import { api } from './api.js';

export const THEMES = [
  // Dark
  { id: 'dark',            label: 'Midnight',        base: 'dark'  },
  { id: 'azure',           label: 'Azure Glow',      base: 'dark'  },
  { id: 'catppuccin',      label: 'Catppuccin',      base: 'dark'  },
  { id: 'dracula',         label: 'Dracula',         base: 'dark'  },
  { id: 'everforest',      label: 'Everforest',      base: 'dark'  },
  { id: 'githubdark',      label: 'GitHub Dark',     base: 'dark'  },
  { id: 'gruvbox',         label: 'Gruvbox',         base: 'dark'  },
  { id: 'kanagawa',        label: 'Kanagawa',        base: 'dark'  },
  { id: 'matteblack',      label: 'Matte Black',     base: 'dark'  },
  { id: 'monokai',         label: 'Monokai',         base: 'dark'  },
  { id: 'nord',            label: 'Nord',            base: 'dark'  },
  { id: 'onedark',         label: 'One Dark',        base: 'dark'  },
  { id: 'osakajade',       label: 'Osaka Jade',      base: 'dark'  },
  { id: 'ristretto',       label: 'Ristretto',       base: 'dark'  },
  { id: 'rosepine',        label: 'Rosé Pine',       base: 'dark'  },
  { id: 'solarizeddark',   label: 'Solarized Dark',  base: 'dark'  },
  { id: 'tokyonight',      label: 'Tokyo Night',     base: 'dark'  },
  // Light
  { id: 'light',           label: 'Daylight',        base: 'light' },
  { id: 'ayulight',        label: 'Ayu Light',       base: 'light' },
  { id: 'latte',           label: 'Catppuccin Latte', base: 'light' },
  { id: 'everforestlight', label: 'Everforest Light', base: 'light' },
  { id: 'flexoki',         label: 'Flexoki',         base: 'light' },
  { id: 'github',          label: 'GitHub',          base: 'light' },
  { id: 'paper',           label: 'Paper',           base: 'light' },
  { id: 'rosepinedawn',    label: 'Rosé Pine Dawn',  base: 'light' },
  { id: 'solarized',       label: 'Solarized',       base: 'light' },
];

const IDS = THEMES.map((t) => t.id);
const baseOf = (id) => THEMES.find((t) => t.id === id)?.base || 'dark';
const KEY = 'ninocat.theme';
const DEFAULT_THEME = 'light'; // 'Daylight' — applied on a device with no saved choice yet

function read() {
  try {
    const t = localStorage.getItem(KEY);
    if (IDS.includes(t)) return t;
  } catch (_) { /* private mode / disabled storage */ }
  return DEFAULT_THEME;
}

function apply(t) {
  try {
    document.documentElement.setAttribute('data-theme', t);
    // Match the PWA / browser title-bar tint to the theme's top bar (--bg-elev-1).
    // In STANDALONE mode the OS title bar is painted from this meta. In WCO mode
    // the strip behind the OS controls is page content (driven by body bg), but
    // some Chromium variants (Brave) still consult theme-color for that strip
    // when toggling between modes — so we set it either way. We toggle through a
    // contrasting value to force a real repaint instead of "no change, ignore".
    const c = getComputedStyle(document.documentElement).getPropertyValue('--bg-elev-1').trim();
    if (c) {
      let m = document.querySelector('meta[name="theme-color"]');
      if (!m) { m = document.createElement('meta'); m.setAttribute('name', 'theme-color'); document.head.appendChild(m); }
      // A two-step set: blank → target. Same-value writes are sometimes ignored
      // by the browser's repaint scheduler; this guarantees a delta.
      m.setAttribute('content', '');
      requestAnimationFrame(() => m.setAttribute('content', c));
    }
  } catch (_) {}
}

// Re-apply when the PWA toggles standalone ↔ window-controls-overlay (the user
// clicks Brave's "collapse chrome" arrow). The OS title-bar paint source changes,
// and Brave doesn't always re-read theme-color on that toggle — re-applying our
// data-theme + meta forces the strip to repick the right value.
if (typeof window !== 'undefined' && window.matchMedia) {
  const onModeChange = () => apply(theme());
  try {
    matchMedia('(display-mode: standalone)').addEventListener('change', onModeChange);
    matchMedia('(display-mode: window-controls-overlay)').addEventListener('change', onModeChange);
  } catch (_) { /* old browsers */ }
}

const [theme, setThemeSignal] = createSignal(read());
apply(theme()); // synchronous at import → no flash-of-wrong-theme on boot

export { theme };

// 'dark' | 'light' for the current theme — used by the editor to match.
export const themeBase = () => baseOf(theme());

// Apply locally (signal + <html> attribute + localStorage cache). Returns true
// if it actually changed. The localStorage cache is what keeps boot flash-free.
function applyLocal(t) {
  if (!IDS.includes(t) || t === theme()) return false;
  setThemeSignal(t);
  apply(t);
  try { localStorage.setItem(KEY, t); } catch (_) {}
  return true;
}

// User picks a theme: apply it AND persist to the account so it follows them to
// other devices (fire-and-forget; the localStorage cache already made it stick).
export function setTheme(t) {
  if (applyLocal(t)) api.put('/api/me/prefs/ui.theme', { value: t }).catch(() => {});
}

// Reconcile the account's saved theme after server prefs load (called by
// loadPrefs). Applies WITHOUT echoing back to the server. On the same device
// this is usually a no-op (localStorage already matched); on a new device it
// swaps the boot default to the user's saved theme.
export function syncThemeFromServer(value) {
  if (value && IDS.includes(value)) applyLocal(value);
}
